import React from 'react';
import { cn } from '@/lib/utils';
import { detectWebView, openInExternalBrowser, getWebViewMessage } from '@/utils/webview';

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo?: string;
}

interface ErrorFallbackProps {
  error: Error;
  resetError: () => void;
}

function ErrorFallback({ error, resetError }: ErrorFallbackProps) {
  const { isWebView, webViewType } = detectWebView();
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-white" style={{ padding: '0 16px' }}>
      <div className="max-w-md w-full text-center">
        <div style={{ marginBottom: '32px' }}>
          <div style={{ fontSize: '64px', marginBottom: '24px' }}>😵</div>
          <h1 style={{ 
            fontSize: '24px', 
            fontWeight: '600', 
            color: '#222222', 
            marginBottom: '16px',
            lineHeight: '1.3'
          }}>
            문제가 발생했어요
          </h1>
          <p style={{ 
            fontSize: '16px', 
            color: '#666666', 
            marginBottom: '32px',
            lineHeight: '1.5'
          }}>
            예상치 못한 오류가 발생했습니다. 다시 시도해주세요.
          </p>
        </div>
        
        <div style={{ 
          display: 'flex', 
          flexDirection: 'column', 
          gap: '16px',
          marginBottom: '24px'
        }}>
          <button
            onClick={resetError}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: '#86DB89',
              color: '#222222',
              fontSize: '16px',
              fontWeight: '600',
              border: 'none',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#76CB79'}
            onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#86DB89'}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            다시 시도
          </button>
          
          <button
            onClick={() => window.location.reload()}
            style={{
              width: '100%',
              padding: '14px 24px',
              backgroundColor: 'transparent',
              color: '#222222',
              fontSize: '16px',
              fontWeight: '600',
              border: '2px solid #DEDEDE',
              borderRadius: '8px',
              cursor: 'pointer',
              transition: 'all 0.2s ease'
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = '#F5F5F5';
              e.currentTarget.style.borderColor = '#CCCCCC';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = 'transparent';
              e.currentTarget.style.borderColor = '#DEDEDE';
            }}
            onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
            onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
          >
            새로고침
          </button>
        </div>
        
        {/* WebView 전용 섹션 추가 */}
        {isWebView && (
          <div style={{
            marginTop: '24px',
            padding: '16px',
            backgroundColor: '#FFF3E0',
            borderRadius: '8px',
            border: '1px solid #FFB74D'
          }}>
            <p style={{
              fontSize: '14px',
              color: '#E65100',
              marginBottom: '12px',
              fontWeight: '600'
            }}>
              {getWebViewMessage(webViewType)}
            </p>
            <p style={{
              fontSize: '13px',
              color: '#666',
              marginBottom: '16px',
              lineHeight: '1.5'
            }}>
              외부 브라우저에서 더 안정적으로 이용하실 수 있습니다.
            </p>
            <button
              onClick={() => openInExternalBrowser()}
              style={{
                width: '100%',
                padding: '12px',
                backgroundColor: '#FF6D00',
                color: 'white',
                fontSize: '14px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                gap: '8px',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#FF5722'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#FF6D00'}
              onMouseDown={(e) => e.currentTarget.style.transform = 'scale(0.98)'}
              onMouseUp={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {/* 외부 링크 아이콘 */}
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M19 19H5V5h7V3H5c-1.11 0-2 .9-2 2v14c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2v-7h-2v7zM14 3v2h3.59l-9.83 9.83 1.41 1.41L19 6.41V10h2V3h-7z" fill="white"/>
              </svg>
              외부 브라우저에서 열기
            </button>
            
            {/* iOS 사용자를 위한 추가 안내 */}
            {detectWebView().isIOS && (
              <p style={{
                fontSize: '11px',
                color: '#999',
                marginTop: '8px'
              }}>
                * iOS에서는 주소가 클립보드에 복사됩니다
              </p>
            )}
          </div>
        )}
        
        {process.env.NODE_ENV === 'development' && (
          <details style={{ marginTop: '32px', textAlign: 'left' }}>
            <summary style={{ 
              cursor: 'pointer', 
              fontSize: '14px', 
              color: '#999999',
              marginBottom: '12px'
            }}>
              에러 정보 (개발 모드)
            </summary>
            <pre style={{ 
              marginTop: '12px', 
              fontSize: '12px', 
              color: '#FF3B30', 
              backgroundColor: '#F5F5F5', 
              padding: '16px', 
              borderRadius: '8px', 
              overflow: 'auto',
              lineHeight: '1.5'
            }}>
              {error.message}
              {'\n\n'}
              {error.stack}
            </pre>
          </details>
        )}
      </div>
    </div>
  );
}

interface ErrorBoundaryProps {
  children: React.ReactNode;
  fallback?: React.ComponentType<{ error: Error; resetError: () => void }>;
  onError?: (error: Error, errorInfo: string) => void;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { hasError: false, error: null };
  }

  static getDerivedStateFromError(error: Error): ErrorBoundaryState {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    const errorString = errorInfo.componentStack || '';
    this.setState({ errorInfo: errorString });
    
    // 에러 로깅
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    // 사용자 정의 에러 처리
    if (this.props.onError) {
      this.props.onError(error, errorString);
    }
  }

  resetError = () => {
    this.setState({ hasError: false, error: null, errorInfo: undefined });
  };

  render() {
    if (this.state.hasError && this.state.error) {
      if (this.props.fallback) {
        const FallbackComponent = this.props.fallback;
        return <FallbackComponent error={this.state.error} resetError={this.resetError} />;
      }
      
      return <ErrorFallback error={this.state.error} resetError={this.resetError} />;
    }

    return this.props.children;
  }
}

// 경량 에러 컴포넌트 (인라인 에러용)
export const InlineError = ({ 
  error, 
  retry, 
  className 
}: { 
  error: string; 
  retry?: () => void; 
  className?: string; 
}) => {
  return (
    <div className={cn(
      'flex flex-col items-center justify-center text-center',
      className
    )} style={{ padding: '32px 16px' }}>
      <div style={{ fontSize: '32px', marginBottom: '12px' }}>⚠️</div>
      <p style={{ 
        fontSize: '16px', 
        color: '#666666', 
        marginBottom: '16px',
        lineHeight: '1.5'
      }}>
        {error}
      </p>
      {retry && (
        <button 
          onClick={retry}
          style={{
            padding: '10px 20px',
            backgroundColor: 'transparent',
            color: '#222222',
            fontSize: '14px',
            fontWeight: '600',
            border: '2px solid #DEDEDE',
            borderRadius: '8px',
            cursor: 'pointer',
            transition: 'all 0.2s ease'
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = '#F5F5F5';
            e.currentTarget.style.borderColor = '#CCCCCC';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = 'transparent';
            e.currentTarget.style.borderColor = '#DEDEDE';
          }}
        >
          다시 시도
        </button>
      )}
    </div>
  );
};

// 네트워크 에러 컴포넌트
export const NetworkErrorBoundary = ({ 
  children, 
  onRetry 
}: { 
  children: React.ReactNode; 
  onRetry?: () => void; 
}) => {
  return (
    <ErrorBoundary
      fallback={({ resetError }) => (
        <div className="flex flex-col items-center justify-center text-center" 
          style={{ padding: '48px 16px' }}>
          <div style={{ fontSize: '48px', marginBottom: '16px' }}>📡</div>
          <h3 style={{ 
            fontSize: '20px', 
            fontWeight: '600', 
            color: '#222222', 
            marginBottom: '12px',
            lineHeight: '1.3'
          }}>
            연결에 문제가 있어요
          </h3>
          <p style={{ 
            fontSize: '16px', 
            color: '#666666', 
            marginBottom: '24px',
            lineHeight: '1.5'
          }}>
            인터넷 연결을 확인하고 다시 시도해주세요
          </p>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button 
              onClick={resetError}
              style={{
                padding: '12px 24px',
                backgroundColor: '#86DB89',
                color: '#222222',
                fontSize: '16px',
                fontWeight: '600',
                border: 'none',
                borderRadius: '8px',
                cursor: 'pointer',
                transition: 'all 0.2s ease'
              }}
              onMouseEnter={(e) => e.currentTarget.style.backgroundColor = '#76CB79'}
              onMouseLeave={(e) => e.currentTarget.style.backgroundColor = '#86DB89'}
            >
              다시 시도
            </button>
            {onRetry && (
              <button 
                onClick={onRetry}
                style={{
                  padding: '12px 24px',
                  backgroundColor: 'transparent',
                  color: '#222222',
                  fontSize: '16px',
                  fontWeight: '600',
                  border: '2px solid #DEDEDE',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  transition: 'all 0.2s ease'
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.backgroundColor = '#F5F5F5';
                  e.currentTarget.style.borderColor = '#CCCCCC';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.backgroundColor = 'transparent';
                  e.currentTarget.style.borderColor = '#DEDEDE';
                }}
              >
                새로고침
              </button>
            )}
          </div>
        </div>
      )}
      onError={(error, errorInfo) => {
        console.error('Network error:', error, errorInfo);
      }}
    >
      {children}
    </ErrorBoundary>
  );
};

export default ErrorBoundary;